import React from 'react';
import { Link } from 'react-router-dom';
import { FiInstagram } from 'react-icons/fi';
import { useSiteSettings } from '../context/SiteSettingsContext';

const Footer = () => {
  const { appearance, storeSettings, content } = useSiteSettings();

  const categories = [
    'Handbags', 'Tote Bags', 'Clutches', 'Backpacks',
    'Wallets', 'Sling Bags', 'Travel Bags', 'Potli Bags',
  ];

  const customerCare = [
    { name: 'Track Order', path: '/account/orders' },
    { name: 'Returns and Exchanges', path: '/returns-policy' },
    { name: 'Shipping Policy', path: '/shipping' },
    { name: 'Terms and Conditions', path: '/terms' },
    { name: 'Privacy Policy', path: '/privacy' },
  ];

  const logoSettings = appearance?.logo || {};

  const getValidLogoUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    let cleanUrl = url.replace(/^["']|["']$/g, '').trim();
    if (/^[A-Za-z]:[/\\]/.test(cleanUrl) || cleanUrl.startsWith('file://')) return null;
    if (cleanUrl.startsWith('http') || cleanUrl.startsWith('data:')) return cleanUrl;
    return `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${cleanUrl.startsWith('/') ? '' : '/'}${cleanUrl}`;
  };

  const logoUrl = getValidLogoUrl(logoSettings.image);

  return (
    <footer id="contact" className="bg-[#1A1A2E] text-white">
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="flex flex-col items-start">
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt={logoSettings.storeName || "Poshe Styles"} 
                className="max-h-14 mb-6 drop-shadow-sm" 
                style={{ filter: 'brightness(0) invert(1) brightness(0.9) sepia(1) hue-rotate(5deg) saturate(2) brightness(1.2)' }} 
              />
            ) : (
              <h2 className="font-playfair text-3xl font-bold text-gold mb-4">
                {logoSettings.storeName || "Poshe Styles"}
              </h2>
            )}
            <p className="text-gray-400 text-sm leading-relaxed mb-8 max-w-xs">
              Carry Your World in Style. Premium bags and accessories crafted for 
              the modern woman who values elegance, quality, and timeless craftsmanship.
            </p>
            
            {/* Social Icons - Kept only Instagram as requested */}
            <div className="flex items-center">
              <a
                href="https://www.instagram.com/poshe_styles"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative flex items-center justify-center w-12 h-12 bg-white/5 rounded-full transition-all duration-500 overflow-hidden border border-white/10 hover:border-transparent"
                title="Follow us on Instagram"
              >
                {/* Instagram Gradient Hover Effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(circle_at_30%_107%,#fdf497_0%,#fdf497_5%,#fd5949_45%,#d6249f_60%,#285AEB_90%)]" />
                <FiInstagram size={22} className="relative z-10 transition-transform duration-500 group-hover:scale-110" />
              </a>
            </div>
          </div>

          {/* Shop by Category */}
          <div>
            <h3 className="font-playfair text-xl font-semibold mb-6 text-gold">
              Shop by Category
            </h3>
            <ul className="space-y-3">
              {categories.map((cat) => (
                <li key={cat}>
                  <Link
                    to={`/products?category=${encodeURIComponent(cat)}`}
                    className="text-gray-400 hover:text-gold text-sm transition-all
                               hover:pl-2 duration-300 inline-block font-inter"
                  >
                    {cat}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Customer Care */}
          <div>
            <h3 className="font-playfair text-xl font-semibold mb-6 text-gold">
              Customer Care
            </h3>
            <ul className="space-y-3">
              {customerCare.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    className="text-gray-400 hover:text-gold text-sm transition-all
                               hover:pl-2 duration-300 inline-block font-inter"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-playfair text-xl font-semibold mb-6 text-gold">
              Get in Touch
            </h3>
            <div className="space-y-5 text-sm">
              <div className="flex items-start gap-4 text-gray-400">
                <span className="text-gold mt-1">📍</span>
                <p className="font-inter leading-relaxed">
                  {storeSettings?.storeAddress || content?.contactPage?.address || 'Pune, Maharashtra, India'}
                </p>
              </div>
              <div className="flex items-center gap-4 text-gray-400">
                <span className="text-gold text-lg">✉️</span>
                <a href={`mailto:${storeSettings?.storeEmail || content?.contactPage?.email || 'hello@poshe-styles.com'}`} className="hover:text-gold transition-colors font-inter">
                  {storeSettings?.storeEmail || content?.contactPage?.email || 'hello@poshe-styles.com'}
                </a>
              </div>
              <div className="flex items-center gap-4 text-gray-400">
                <span className="text-gold text-lg">📞</span>
                <a href={`tel:${storeSettings?.storePhone || content?.contactPage?.phone || '+91 98765 43210'}`} className="hover:text-gold transition-colors font-inter">
                  {storeSettings?.storePhone || content?.contactPage?.phone || '+91 98765 43210'}
                </a>
              </div>
              <div className="flex items-center gap-4 text-gray-400">
                <span className="text-gold text-lg">🕒</span>
                <p className="font-inter">
                  {content?.contactPage?.businessHours || 'Mon – Sat: 10:00 AM – 7:00 PM'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/5 bg-[#0A0A14]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-gray-500 text-xs font-inter tracking-wide uppercase">
              © 2026 Poshe Styles. All rights reserved. | <Link to="/terms" className="hover:text-gold">Terms</Link> | <Link to="/privacy" className="hover:text-gold">Privacy</Link>
            </p>
            <div className="flex items-center gap-6 saturate-0 opacity-40 hover:saturate-100 hover:opacity-100 transition-all duration-500">
              <span className="text-[10px] font-bold tracking-widest uppercase text-gray-500 border border-gray-800 px-2 py-1 rounded">Razorpay</span>
              <span className="text-[10px] font-bold tracking-widest uppercase text-gray-500 border border-gray-800 px-2 py-1 rounded">UPI</span>
              <span className="text-[10px] font-bold tracking-widest uppercase text-gray-500 border border-gray-800 px-2 py-1 rounded">VISA</span>
              <span className="text-[10px] font-bold tracking-widest uppercase text-gray-500 border border-gray-800 px-2 py-1 rounded">Mastercard</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
